import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { syncUser } from '../services/auth';
import { storage } from '../services/storage';
import '../styles/Auth.css';

const CURRICULA = [
  { value: 'grade6', label: 'Grade 6 Math' },
  { value: 'grade7', label: 'Grade 7 Math' },
  { value: 'grade8', label: 'Grade 8 Math' },
  { value: 'algebra1', label: 'Algebra 1 (Grade 9)' },
  { value: 'math1', label: 'Mathematics I (Grade 9)' },
];

export const AddGradePage: React.FC = () => {
  const { user, availableCurricula, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [selectedCurriculum, setSelectedCurriculum] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter out curricula the user already has
  const enrolledCurricula = new Set(availableCurricula.map(c => c.curriculum));
  const availableToAdd = CURRICULA.filter(c => !enrolledCurricula.has(c.value));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCurriculum) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Call sync with the new curriculum to create the student record
      const response = await syncUser('student', selectedCurriculum);

      // Update storage with the new curriculum
      storage.setCurriculum(selectedCurriculum);
      if (response.user.student_id) {
        storage.setStudentId(response.user.student_id);
      }

      // Refresh user data
      await refreshUser();

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add grade');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (availableToAdd.length === 0) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>Add Another Grade</h1>
            <p>You're already enrolled in all available grades!</p>
          </div>
          <button
            className="auth-button primary"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>Add Another Grade</h1>
          <p>Select a new grade to add to your account</p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.5rem' }}>
            Currently enrolled in:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {availableCurricula.map(c => (
              <span
                key={c.curriculum}
                style={{
                  background: '#e0e7ff',
                  color: '#4338ca',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                }}
              >
                {c.display_name}
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="curriculum">Select Grade to Add</label>
            <select
              id="curriculum"
              value={selectedCurriculum}
              onChange={(e) => setSelectedCurriculum(e.target.value)}
              disabled={isSubmitting}
              className="form-select"
              style={{
                padding: '0.75rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                width: '100%',
              }}
            >
              <option value="">Choose a grade...</option>
              {availableToAdd.map(c => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isSubmitting || !selectedCurriculum}
          >
            {isSubmitting ? 'Adding Grade...' : 'Add Grade'}
          </button>

          <button
            type="button"
            className="auth-button secondary"
            onClick={() => navigate('/dashboard')}
            disabled={isSubmitting}
            style={{ marginTop: '0.5rem' }}
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};
